/*
Discover-next suggestion engine.

Given the user's current GPS fix and their capture state, surfaces
the closest tree they should head toward next. Priorities, in order:

  1. **New species** — closest tree of a species the user has never
     captured. Maximises Arboretum growth, the obvious "next thing
     to find".
  2. **Variety** — closest tree of a species the user has captured
     exactly once. Encourages the user to revisit the same species
     in different settings, which is what the Android app's
     "Tourneur" badge family rewards.

Returns null when no candidate is in the dataset (e.g., the user
has captured every species AND every species has been captured at
least twice). The map screen surfaces the result through the
SuggestionCard component; null hides the card.

The scan iterates every feature once (~213 k entries), with a
constant-time category lookup per row. ~30-50 ms on a phone — fine
to recompute on every GPS update without debouncing.
*/

import type { ArbreFeature } from './arbresFeatures'
import type { Capture } from './types'
import { speciesNameFor } from './speciesIndex'

export type SuggestionKind = 'new-species' | 'variety'

export interface DiscoverSuggestion
{
	kind: SuggestionKind
	feature: ArbreFeature
	distanceMeters: number
	speciesIndex: number
	primaryLabel: string  // common name when known, latin binomial otherwise
	secondaryLabel: string  // empty string when nothing useful to say
}

/*
Compute the suggestion. `inFeatures` is the lightweight projection
already loaded by `arbresFeatures.ts`; `inCaptures` is the full
captures list (we project per-species counts internally so the
caller doesn't have to).
*/
export function computeDiscoverSuggestion(
	inFix: { lat: number; lon: number },
	inFeatures: readonly ArbreFeature[],
	inCaptures: readonly Capture[],
): DiscoverSuggestion | null
{
	if (inFeatures.length === 0) return null
	const vCapturedArbreIds = new Set<number>(inCaptures.map((inC) => inC.arbreId))
	const vSpeciesCounts = new Map<number, number>()  // sk -> total captures across all sessions
	for (const vCap of inCaptures)
	{
		vSpeciesCounts.set(vCap.speciesIndex, (vSpeciesCounts.get(vCap.speciesIndex) ?? 0) + 1)
	}

	// Two parallel running-best slots so a single linear pass picks
	// both winners. Squared distance for the comparison; we convert
	// the winning slot to meters at the end.
	let vBestNewSq: number = Number.POSITIVE_INFINITY
	let vBestNewFeature: ArbreFeature | null = null
	let vBestVarietySq: number = Number.POSITIVE_INFINITY
	let vBestVarietyFeature: ArbreFeature | null = null

	for (const vF of inFeatures)
	{
		if (vCapturedArbreIds.has(vF.id)) continue  // already captured this exact tree — skip both buckets
		const vCount = vSpeciesCounts.get(vF.speciesIndex) ?? 0
		// Project to local-tangent metres. Paris fits in a 12 km
		// square so a flat-earth approximation around the user's lat
		// is accurate to within a fraction of a metre — far below the
		// GPS noise floor. Saves us from the haversine `sin/cos`
		// hot-path during a 213k iteration.
		const vDLat = vF.lat - inFix.lat
		const vDLon = (vF.lon - inFix.lon) * Math.cos(inFix.lat * Math.PI / 180)
		const vSq = vDLat * vDLat + vDLon * vDLon
		if (vCount === 0)
		{
			if (vSq < vBestNewSq) { vBestNewSq = vSq; vBestNewFeature = vF }
		}
		else if (vCount === 1)
		{
			if (vSq < vBestVarietySq) { vBestVarietySq = vSq; vBestVarietyFeature = vF }
		}
	}

	const vChoice: { feature: ArbreFeature; sq: number; kind: SuggestionKind } | null =
		vBestNewFeature !== null
			? { feature: vBestNewFeature, sq: vBestNewSq, kind: 'new-species' }
			: vBestVarietyFeature !== null
				? { feature: vBestVarietyFeature, sq: vBestVarietySq, kind: 'variety' }
				: null
	if (!vChoice) return null

	// Convert the squared local-tangent distance to meters.
	// 1° ≈ 111 320 m in latitude, with the longitude factor already
	// folded into vDLon above; multiply by 111 320 to land in metres.
	const vMetres = Math.sqrt(vChoice.sq) * 111_320

	const vName = speciesNameFor(vChoice.feature.speciesIndex)
	const vPrimary = vName?.commonName ?? formatLatin(vName)
	const vSecondary = vName?.commonName ? formatLatin(vName) : ''

	return {
		kind: vChoice.kind,
		feature: vChoice.feature,
		distanceMeters: vMetres,
		speciesIndex: vChoice.feature.speciesIndex,
		primaryLabel: vPrimary,
		secondaryLabel: vSecondary,
	}
}

function formatLatin(inName: { genre: string; espece: string } | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}
